import ProgressBar from "@/components/ProgressBar/ProgressBar";
import Skeleton from "@/components/Skeleton/Skeleton";
import NoDataPoints from "@/components/visualizations/NoDataPoints/NoDataPoints";
import { ImpactMetric, ImpactMetricGroupIcons } from "@/pages/dashboard/utils/types";
import { Flex, Space } from "antd";
import _ from "lodash";
import useVisualization from "../../../../components/visualizations/useVisualization";
import Widget, { WidgetProps } from "../Widget/Widget";
import styles from "./MetricWidget.module.scss";

export type MetricWidgetData =
  | { value?: number; comparison?: { value?: number; label: string } }
  | undefined;

export interface MetricWidgetProps extends Omit<WidgetProps, "children"> {
  data?: MetricWidgetData;
  format?: (value: number) => string;
  type?: "vertical";
  metric?: ImpactMetric;
}

export default function MetricWidget(props: MetricWidgetProps) {
  const { data, loading } = props;
  const { formatLabel } = useVisualization(props);
  const metricIcon =
    props.metric?.icon || ImpactMetricGroupIcons[props.metric?.impactGroup || "Main impacts"];
  const percentage = data?.value && data.comparison?.value ? data.value / data.comparison.value : 0;

  const content = (
    <div>
      {loading ? (
        <Space
          direction={"vertical"}
          align={"end"}
        >
          <Skeleton
            style={{
              width: "120px",
              height: "34px",
            }}
          />
        </Space>
      ) : data?.value === undefined ? (
        <NoDataPoints />
      ) : (
        <Space direction={"vertical"}>
          <Flex
            align={"center"}
            justify={"right"}
            gap={12}
            className={styles.metric}
            style={{ marginBottom: -2 }}
          >
            <span style={{ fontSize: 24, marginBottom: -2 }}>{metricIcon}</span>
            <span>{formatLabel(data.value, true)}</span>
          </Flex>
        </Space>
      )}
    </div>
  );

  let children = undefined;
  if (props.type === "vertical" || data?.comparison) {
    children = (
      <>
        {props.type === "vertical" && content}
        {data?.comparison && (
          <div className={styles.compareContainer}>
            {loading ? (
              <>
                <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                  <Skeleton
                    style={{
                      width: "163px",
                      height: "22px",
                    }}
                  />
                  <Skeleton
                    style={{
                      width: "100%",
                      height: "22px",
                    }}
                  />
                </div>
              </>
            ) : (
              <div className={styles.compare}>
                <span style={{ minWidth: "max-content" }}>{`${_.round(
                  (percentage || 0) * 100,
                  2
                )}% ${data.comparison.label}`}</span>
                <div style={{ width: "100%" }}>
                  <ProgressBar
                    showPercentage={false}
                    value={
                      data.value && data.comparison.value ? data.value / data.comparison.value : 0
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <Widget
      {...props}
      contentClassName={[styles.Metric, styles[props.type || ""]].join(" ")}
      topRight={props.type !== "vertical" && content}
      footer={children}
    />
  );
}
